import {
  APIPaginatedRequest,
  CountRequest,
  ListEventsRequest,
} from "shared/api/api";
import { clientV2 } from "shared/api/axios";
import {
  SensorReadingsTimelineAggregation,
  SensorReadingsTimelineGrouping,
} from "shared/api/sensors/api";
import { createURL } from "shared/api/utils";
import { CountRequestWithVehiclesFilter } from "shared/api/vehicles/api";

const SIGNAL_EVENTS_ROUTE = "signalEvents";

// Endpoint key for filter value lookups (consumed by genericValuesCallback,
// which routes the `v2/` prefix to the V2 /values/:field route).
export const SIGNAL_EVENTS_V2_VALUES_ENDPOINT = "v2/signalEvents/occurrences";

// V2 owns its own request/response types (keyed on assetID, trimmed field set)
// so a future V1 removal is a no-op here. Only the fields the V2 surface
// actually returns are declared.
export interface ListSignalEventOccurrencesVINTimelineRequest extends APIPaginatedRequest {
  grouping?: SensorReadingsTimelineGrouping;
  aggregation?: SensorReadingsTimelineAggregation;
  skipRequest?: boolean;
}

export type ListSignalEventOccurrencesVINAggregateRequest = APIPaginatedRequest;

// /occurrences/timeline bucket: occurrences (`count`) per `time`, per asset.
export interface SignalEventOccurrencesV2TimelineBucket {
  assetID: string;
  signalEventID: string;
  signalEventType: string;
  time: string;
  count: number;
}

// /occurrences/aggregate bucket: total occurrences per (asset, signal event).
export interface SignalEventOccurrencesAggregateBucket {
  assetID: string;
  signalEventID: string;
  signalEventType: string;
  totalOccurrences: number;
  description?: string;
}

// /occurrences raw row (trimmed V2 column set; recordedAt/occurrenceCount).
export interface SignalEventsOccurrence {
  assetID: string;
  signalEventID: string;
  signalEventType: string;
  recordedAt: string;
  ingestedAt: string | null;
  occurrenceCount: number;
  description: string | null;
  spn: number | null;
  fmi: number | null;
  obd2: string | null;
  warningIndicator: boolean | null;
  milLamp: boolean | null;
  redLamp: boolean | null;
  amberLamp: boolean | null;
  protectLamp: boolean | null;
  status: string | null;
}

export const listSignalEventOccurrencesVINTimelineRequestURI = ({
  skipRequest,
  ...params
}: ListSignalEventOccurrencesVINTimelineRequest): string =>
  skipRequest
    ? ""
    : clientV2.getUri({
        method: "GET",
        url: createURL([SIGNAL_EVENTS_ROUTE, "occurrences", "timeline"]),
        params,
      });

export const listSignalEventOccurrencesVINAggregateRequestURI = ({
  ...params
}: ListSignalEventOccurrencesVINAggregateRequest): string =>
  clientV2.getUri({
    method: "GET",
    url: createURL([SIGNAL_EVENTS_ROUTE, "occurrences", "aggregate"]),
    params,
  });

export const listSignalEventOccurrencesVINAggregateCountRequestURI = ({
  ...params
}: CountRequest): string =>
  clientV2.getUri({
    method: "GET",
    url: createURL([SIGNAL_EVENTS_ROUTE, "occurrences", "aggregate", "count"]),
    params,
  });

export const listSignalEventsOccurrencesRequestURI = ({
  skipRequest,
  ...params
}: ListEventsRequest): string =>
  skipRequest
    ? ""
    : clientV2.getUri({
        method: "GET",
        url: createURL([SIGNAL_EVENTS_ROUTE, "occurrences"]),
        params,
      });

export const listSignalEventsOccurrencesCountRequestURI = ({
  ...params
}: CountRequestWithVehiclesFilter): string =>
  clientV2.getUri({
    method: "GET",
    url: createURL([SIGNAL_EVENTS_ROUTE, "occurrences", "count"]),
    params,
  });

export const listSignalEventOccurrencesAttributesRequestURI = (): string =>
  clientV2.getUri({
    method: "GET",
    url: createURL([SIGNAL_EVENTS_ROUTE, "occurrences", "attributes"]),
  });
